import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  MemberCard,
  MemberCardQuery,
  MemberDetails,
  MemberQuery,
  BalanceQuery,
  config,
  CardToken,
  CardTokenQuery,
  CardTokenService,
  FeedQuery,
} from '@fgb/core';
import { ScreenType } from '@fgb/portal-component-library/src/lib/directives';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Location } from '@angular/common';
import { ProductCode } from 'src/app/shared/utilities/product-code';

@Component({
  selector: 'fgb-mobile-page-header',
  templateUrl: './mobile-page-header.component.html',
  styleUrls: ['./mobile-page-header.component.scss'],
})
export class MobilePageHeaderComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() showAfter: boolean = true;
  @Input() backButtonLink?: string;
  @Input() pageTitle: string;
  @Input() isHomePage: boolean = false;
  @Input() showBalance: boolean = true;
  @ViewChild('mobileNavigation') mobileNavigationRef?: ElementRef<HTMLElement>;
  screenType = ScreenType;
  productCode = ProductCode;
  memberCard$: Observable<MemberCard | undefined>;
  memberDetails$: Observable<MemberDetails | undefined>;
  loyaltyPointsCard$: Observable<number | undefined>;
  cardToken$: Observable<CardToken | undefined>;
  hasLoyaltyFeed$: Observable<boolean>;
  hasOverflow = false;
  isScrolledToEnd = false;
  private resizeObserver?: ResizeObserver;

  constructor(
    private memberCardQuery: MemberCardQuery,
    private memberQuery: MemberQuery,
    private router: Router,
    private location: Location,
    private balanceQuery: BalanceQuery,
    private cardTokenQuery: CardTokenQuery,
    private cardTokenService: CardTokenService,
    private feedQuery: FeedQuery,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.memberCard$ = this.memberCardQuery.selectCurrentCard();
    this.memberDetails$ = this.memberQuery.selectMemberDetails();
    this.loyaltyPointsCard$ = this.balanceQuery.selectPurse(config.purseConfig.virtualLoyalty);
    firstValueFrom(this.cardTokenService.fetchCardTokens());
    this.cardToken$ = this.cardTokenQuery
      .selectCard()
      .pipe(map((cardTokens) => cardTokens.find((cardToken) => cardToken.IsDefaultToken) ?? cardTokens[0]));
    this.hasLoyaltyFeed$ = this.feedQuery.dynamicLoyaltyFeeds$.pipe(map((feeds) => !!feeds && feeds.length > 0));
  }

  ngAfterViewChecked(): void {
    const el = this.mobileNavigationRef?.nativeElement;
    if (el && !this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.checkOverflow());
      this.resizeObserver.observe(el);
      this.checkOverflow();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  nameSizeClass(fullName: string): string {
    const length = fullName.trim().length;
    if (length > 26) {
      return 'card-name-xs';
    }
    if (length > 18) {
      return 'card-name-sm';
    }
    return '';
  }

  goBack() {
    if (this.backButtonLink) {
      this.router.navigate([this.backButtonLink]);
    } else {
      this.location.back();
    }
  }

  onNavigationScroll(): void {
    this.checkOverflow();
  }

  private checkOverflow(): void {
    const el = this.mobileNavigationRef?.nativeElement;
    if (!el) {
      return;
    }
    const hasOverflow = el.scrollWidth > el.clientWidth;
    const isScrolledToEnd = Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth - 1;
    if (hasOverflow !== this.hasOverflow || isScrolledToEnd !== this.isScrolledToEnd) {
      this.hasOverflow = hasOverflow;
      this.isScrolledToEnd = isScrolledToEnd;
      this.cdr.detectChanges();
    }
  }
}
